import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography,
} from '@mui/material';

import { anfrageErledigen, anfrageZuordnen, kundenListe, uebersicht } from '../api';
import { Seitenkopf } from '../komponenten/Bausteine';
import { fehlertext } from '../utils/format';

const STREIFEN = { 1: '#EF4444', 2: '#F59E0B', 3: '#9CA3AF' };
const KNOPF = {
  verlaengern: 'Verlängern', geraet: 'Gerät wechseln', kontingent: 'Kontingent ändern',
  code: 'Akte öffnen', akte: 'Akte öffnen', anfrage_erledigen: 'Erledigt',
};

function Zahl({ wert, text, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: 'primary.main' } : undefined }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{wert}</Typography>
        <Typography variant="body2" color="text.secondary">{text}</Typography>
      </CardContent>
    </Card>
  );
}

// Anfrage ohne erkannten Kunden (kam ohne oder mit unbekannter Hardware-ID):
// Kunden waehlen, danach geht es in dessen Akte mit offenem Verlaengern-Dialog.
function ZuordnenDialog({ anfrage, onZu, onFertig }) {
  const [kunden, setKunden] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    if (!anfrage) return;
    setKunde(null);
    setFehler('');
    kundenListe().then((r) => {
      setKunden(r.data);
      // Vorschlag: gleicher Name wie in der Anfrage
      const name = (anfrage.name || '').trim().toLowerCase();
      setKunde(r.data.find((k) => [k.anzeige, k.name].some((n) => (n || '').trim().toLowerCase() === name)) || null);
    }).catch((e) => setFehler(fehlertext(e, 'Kunden konnten nicht geladen werden.')));
  }, [anfrage]);

  const speichern = async () => {
    try {
      await anfrageZuordnen(anfrage.anfrage_id, kunde.id);
      onFertig(kunde.id);
    } catch (e) {
      setFehler(fehlertext(e));
    }
  };

  return (
    <Dialog open={!!anfrage} onClose={onZu} fullWidth maxWidth="sm">
      <DialogTitle>Anfrage einem Kunden zuordnen</DialogTitle>
      <DialogContent>
        {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {anfrage?.titel}<br />{anfrage?.detail}
        </Typography>
        <Autocomplete
          options={kunden}
          value={kunde}
          onChange={(_, k) => setKunde(k)}
          getOptionLabel={(k) => (k.anzeige !== k.name ? `${k.anzeige} (${k.name})` : k.anzeige)}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(p) => <TextField {...p} label="Kunde" autoFocus />}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onZu}>Abbrechen</Button>
        <Button variant="contained" disabled={!kunde} onClick={speichern}>Zuordnen und verlängern</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Uebersicht() {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [zuordnen, setZuordnen] = useState(null);

  const laden = useCallback(() => {
    uebersicht().then((r) => { setDaten(r.data); setFehler(''); }).catch((e) => setFehler(fehlertext(e, 'Übersicht konnte nicht geladen werden.')));
  }, []);
  useEffect(() => {
    laden();
    const id = setInterval(laden, 60000);
    return () => clearInterval(id);
  }, [laden]);

  const ausfuehren = async (a) => {
    if (a.aktion === 'anfrage_erledigen') {
      try { await anfrageErledigen(a.anfrage_id); laden(); } catch (e) { setFehler(fehlertext(e)); }
      return;
    }
    if (!a.kunde_id) { navigate('/installationen'); return; }
    const p = new URLSearchParams();
    if (['verlaengern', 'geraet', 'kontingent'].includes(a.aktion)) p.set('aktion', a.aktion);
    if (a.lizenz_id) p.set('lizenz', a.lizenz_id);
    if (a.hardware_id) p.set('hw', a.hardware_id);
    navigate(`/kunden/${a.kunde_id}${p.toString() ? `?${p}` : ''}`);
  };

  if (!daten) return fehler ? <Alert severity="error">{fehler}</Alert> : null;
  const z = daten.zahlen;

  return (
    <Box>
      <Seitenkopf titel="Übersicht" untertitel="Was heute zu tun ist, das Dringendste zuerst." />
      {fehler && <Alert severity="error" sx={{ mb: 2 }}>{fehler}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Zahl wert={z.kunden} text="Kunden" onClick={() => navigate('/kunden')} />
        <Zahl wert={z.lizenzen_aktiv} text="Lizenzen aktiv" onClick={() => navigate('/kunden')} />
        <Zahl
          wert={z.neueste_version ? `${z.installationen_aktuell} / ${z.installationen}` : z.installationen}
          text={z.neueste_version ? `auf Version ${z.neueste_version}` : 'Installationen'}
          onClick={() => navigate('/installationen')}
        />
        <Zahl wert={z.still} text={z.still === 1 ? 'meldet sich nicht' : 'melden sich nicht'} onClick={() => navigate('/installationen')} />
      </Box>

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: '0.08em' }}>Heute zu tun</Typography>
          {daten.aufgaben.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 600 }}>Nichts zu tun.</Typography>
              <Typography variant="body2" color="text.secondary">
                Keine Lizenz läuft in den nächsten 30 Tagen ab, alle Installationen melden sich.
              </Typography>
            </Box>
          ) : daten.aufgaben.map((a, i) => (
            <Box
              key={`${a.art}-${a.lizenz_id}-${a.anfrage_id}-${i}`}
              sx={{
                display: 'grid', gridTemplateColumns: { xs: '8px minmax(0, 1fr)', sm: '8px minmax(0, 1fr) auto' },
                columnGap: 1.5, rowGap: 1, alignItems: 'center', py: 1.5,
                borderTop: i === 0 ? 'none' : '1px solid', borderColor: 'divider',
              }}
            >
              <Box sx={{ width: 8, alignSelf: 'stretch', minHeight: 36, borderRadius: 1, bgcolor: STREIFEN[a.stufe] }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{a.titel}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{a.detail}</Typography>
              </Box>
              <Box sx={{ gridColumn: { xs: '2', sm: 'auto' }, display: 'flex', gap: 1 }}>
                {a.aktion === 'anfrage_erledigen' && (a.kunde_id
                  ? <Button size="small" variant="contained" onClick={() => navigate(`/kunden/${a.kunde_id}?aktion=verlaengern`)}>Verlängern</Button>
                  : <Button size="small" variant="contained" onClick={() => setZuordnen(a)}>Kunde zuordnen</Button>)}
                <Button size="small" variant={a.aktion === 'verlaengern' ? 'contained' : 'outlined'} onClick={() => ausfuehren(a)}>
                  {a.kunde_id || a.aktion === 'anfrage_erledigen' ? KNOPF[a.aktion] : 'Installationen'}
                </Button>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      <ZuordnenDialog
        anfrage={zuordnen}
        onZu={() => setZuordnen(null)}
        onFertig={(kundeId) => { setZuordnen(null); navigate(`/kunden/${kundeId}?aktion=verlaengern`); }}
      />

      {z.testphase > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {z.testphase} {z.testphase === 1 ? 'Installation läuft' : 'Installationen laufen'} gerade in der 14-Tage-Testphase ohne Lizenz –{' '}
          <Box component="span" sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/installationen')}>ansehen</Box>.
        </Typography>
      )}
    </Box>
  );
}
